import { Pressable, SectionList } from "react-native";
import { customColorTokens } from "../../../tamagui.config";
import {
  AnimatePresence,
  ScrollView as TGScrollView,
  Text,
  View,
  XStack,
} from "tamagui";
import { EventCard } from "../../components/design-system/EventCard";

export const ScrollView = motify(TGScrollView)();

import { format, parseISO } from "date-fns";
import { motify } from "moti";
import { EventType, Group } from "../../features/Events/types";
import { useMemo, useState } from "react";
import { Pill } from "../../components/design-system/Pill";
import {
  CloseIcon,
  PartyGroupIcon,
} from "../../components/design-system/icons";
import { paceOptions, paces } from "../Community/components/Filters";
import { Loader } from "../../components/design-system/Loader";
import { Event } from "../../types/event";
import { FilterSheet } from "../../components/design-system/FilterSheet/FilterSheet";
import { EventImage } from "./components/EventImage";
import { useApiQuery } from "../../../lib/api";

const eventTypes: EventType[] = [
  "CentralMachineWorks",
  "GravelGang",
  "PopUp",
  "TheMeteorCafe",
  "Babes",
  "SwimClub",
];

export const EventsList = () => {
  const today = new Date();
  const date = format(today, "yyyy-MM-dd");

  const [eventType, setEventType] = useState<EventType[]>(eventTypes);
  const [paceFilter, setPaceFilter] = useState<Group[]>(paceOptions);

  const queryParams = new URLSearchParams();
  queryParams.set("type", eventType.join(","));
  queryParams.set("groups", paceFilter.join(","));
  queryParams.set("date_from", date);

  const { data, isLoading, refetch, isRefetching } = useApiQuery<{ events: Event[] }>(
    ["events-list", eventType.join(","), paceFilter.join(","), date],
    `/api/v1/events?${queryParams.toString()}`
  );

  const events = data?.events ?? null;

  const groupedData = events?.reduce((acc: Record<string, Event[]>, curr) => {
    const dateString = curr.date ?? null;
    const eventDate = dateString ? parseISO(dateString) : new Date();
    const eventMonthYear = dateString ? format(eventDate, "MMMM yyyy") : "";

    if (!acc[eventMonthYear]) {
      acc[eventMonthYear] = [];
    }
    acc[eventMonthYear].push(curr);
    return acc;
  }, {});

  const newSections = groupedData
    ? Object.keys(groupedData).map((key) => ({
        title: key,
        data: groupedData[key],
      }))
    : [];

  const renderSectionHeader = ({
    section: { title },
  }: {
    section: {
      title: string;
    };
  }) => (
    <View bg="$brandParchment" p="$4">
      <Text fontSize={16} fontFamily="$heading" textTransform="uppercase">
        {title}
      </Text>
    </View>
  );

  const renderItem = ({ item }: { item: Event }) => {
    return (
      <View px="$4" mb="$4">
        <EventCard event={item} />
      </View>
    );
  };

  const allPacesSelected =
    paceFilter?.length === paces.length || paces?.length === 0;

  const allEventTypesSelected =
    eventType?.length === eventTypes.length || eventTypes?.length === 0;

  const [isPaceSheetOpen, setIsPaceSheetOpen] = useState(false);

  const [isEventTypeSheetOpen, setIsEventTypeSheetOpen] = useState(false);

  const renderPills = useMemo(
    () => (
      <XStack
        gap="$2"
        bg="$brandParchment"
        flexWrap="wrap"
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        <AnimatePresence>
          <Pressable
            onPress={() => setIsEventTypeSheetOpen(true)}
            key="event-type"
          >
            <Pill
              text={
                allEventTypesSelected
                  ? "Events"
                  : `${eventType?.map((p) => p).join(", ")} Events`
              }
              bg={"$brandYellowMuted"}
              icon={
                allEventTypesSelected ? undefined : (
                  <CloseIcon width={12} height={12} />
                )
              }
              color={"black"}
              borderColor={allEventTypesSelected ? "#EBE7DE" : "black"}
              hideShadow={true}
            />
          </Pressable>

          <Pressable onPress={() => setIsPaceSheetOpen(true)} key="terrain">
            <Pill
              text={
                allPacesSelected
                  ? "Pace"
                  : `${paceFilter?.map((p) => p).join(", ")} Pace`
              }
              bg={"$brandYellowMuted"}
              icon={
                allPacesSelected ? undefined : (
                  <CloseIcon width={12} height={12} />
                )
              }
              color={"black"}
              borderColor={allPacesSelected ? "#EBE7DE" : "black"}
              hideShadow={true}
            />
          </Pressable>
        </AnimatePresence>
      </XStack>
    ),
    [paceFilter, allPacesSelected, eventType, allEventTypesSelected]
  );

  return (
    <View flex={1}>
      {isLoading ? <Loader /> : null}
      <SectionList
        refreshing={isRefetching}
        onRefresh={refetch}
        ListHeaderComponent={() => (
          <ScrollView
            bg="$brandParchment"
            px="$4"
            horizontal={true}
            showsHorizontalScrollIndicator={false}
          >
            <XStack mt="$2" gap="$2" alignItems="center" flexWrap="wrap">
              <Text
                fontSize={16}
                fontFamily="$heading"
                textTransform="uppercase"
              >
                Filter By
              </Text>
              {renderPills}
            </XStack>
          </ScrollView>
        )}
        sections={newSections}
        keyExtractor={(item) => item.id ?? ""}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        stickySectionHeadersEnabled={true}
        style={{ backgroundColor: customColorTokens.brandParchment }}
        contentContainerStyle={{
          paddingBottom: 100,
        }}
        ListEmptyComponent={
          <View flex={1} justifyContent="center" alignItems="center" mt="$16">
            <Text fontSize={16}>{`ON HOLIDAY`}</Text>
            <Text fontSize={16}>{`See you in 2026!`}</Text>
          </View>
        }
      />

      <FilterSheet
        isSheetOpen={isPaceSheetOpen}
        setIsSheetOpen={setIsPaceSheetOpen}
        handleApply={(selectedOptions) => {
          setPaceFilter(selectedOptions as Group[]);
          setIsPaceSheetOpen(false);
        }}
        title="Choose Pace"
        options={paces.map((p) => ({
          label: p.label,
          value: p.value,
          detail: p.detail,
          icon:
            p.value === "Party" ? (
              <PartyGroupIcon fill="#F7EAC4" width={32} height={32} />
            ) : (
              <Text
                fontSize={24}
                textTransform="uppercase"
                color="black"
                fontFamily="$heading"
              >
                {p.value}
              </Text>
            ),
        }))}
      />

      <FilterSheet
        isSheetOpen={isEventTypeSheetOpen}
        setIsSheetOpen={setIsEventTypeSheetOpen}
        handleApply={(selectedOptions) => {
          setEventType(selectedOptions as EventType[]);
          setIsEventTypeSheetOpen(false);
        }}
        title="Choose Event Type"
        options={eventTypes.map((p) => ({
          label: humanizeType(p),
          value: p,
          detail: "",
          icon: <EventImage type={p} width={24} height={24} />,
        }))}
      />
    </View>
  );
};

const humanizeType = (type: EventType) => {
  switch (type) {
    case "CentralMachineWorks":
      return "CMW";
    case "GravelGang":
      return "Gravel Gang";
    case "PopUp":
      return "Pop Up";
    case "TheMeteorCafe":
      return "The Meteor";
    case "Babes":
      return "Babes";
    case "SwimClub":
      return "Swim Club";
    default:
      return "All";
  }
};
